import React, { useState, useEffect, useCallback } from 'react';
import { Container } from 'react-bootstrap';
import { useApolloClient, useMutation } from '@apollo/client';
import styles from './styles.module.scss';
import TabToggleButtons from './components/TabToggleButtons';
import DocumentFieldsList from './DocumentFieldsList';
import DealFieldsList from './DealFieldsList';
import CreatePlaceholderModal from './components/CreatePlaceholderModal';
import { ContactPickerProvider, useContactPicker } from './ContactPickerContext';
import { FieldHistoryProvider, useFieldHistory } from './FieldHistoryContext';
import FieldHistorySlideout from './components/FieldHistorySlideout';
import ContactPickerOverlay from './components/ContactPickerOverlay';
import ContactSlideout from '../../contacts/ContactSlideout';
import { GET_DOCUMENT_PLACEHOLDER_LINKS, GET_DEAL_FIELD_GROUPS } from '../../../api/placeholders/queries';
import { mutations } from '../../../api';
import { useUserRestrictions } from '../../../auth/user-restrictions';
import SubscriptionRequiredModal from '../../../pages/modals/subscriptionModal/SubscriptionRequiredModal';

export enum PlaceholdersSidebarTab {
  documentFields = 'documentFields',
  dealFields = 'dealFields',
}

interface PlaceholdersSidebarContainerProps {
  documentId: string;
  dealId: string;
  teamId: string;
  isReadOnly?: boolean;
  isCounterparty?: boolean; // Simplified view for counterparty team
}

// Inner component that uses the ContactPickerContext
const PlaceholdersSidebarContent: React.FC<PlaceholdersSidebarContainerProps> = ({
  documentId,
  dealId,
  teamId,
  isReadOnly = false,
  isCounterparty = false,
}) => {
  const [activeTab, setActiveTab] = useState<PlaceholdersSidebarTab>(PlaceholdersSidebarTab.documentFields);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [canInsertAtCursor, setCanInsertAtCursor] = useState(false); // Default false until CKEditor confirms
  const { editContactData, closeEditContact } = useContactPicker();
  const { historyTarget, closeHistory } = useFieldHistory();
  const apolloClient = useApolloClient();

  // Use standard subscription check - canCreateTemplates is the premium feature gate
  const { canCreateTemplates } = useUserRestrictions();
  const canCreatePlaceholders = canCreateTemplates;

  // Mutation to create a new placeholder
  const [createDocumentPlaceholder] = useMutation(mutations.createDocumentPlaceholder, {
    refetchQueries: [
      { query: GET_DOCUMENT_PLACEHOLDER_LINKS, variables: { documentId } },
    ],
  });

  // Handle contact save - refetch queries to update placeholder values and sync to the editor
  const handleContactSave = async () => {
    // Close the slideout
    closeEditContact();

    // Force fetch fresh data for document placeholder links (network-only bypasses cache).
    // This is needed because when on "This Deal" tab, DocumentFieldsList isn't mounted
    // so the query isn't being watched and refetchQueries won't update it.
    try {
      const { data: docData } = await apolloClient.query({
        query: GET_DOCUMENT_PLACEHOLDER_LINKS,
        variables: { documentId },
        fetchPolicy: 'network-only',
      });

      // Also refetch deal field groups for the sidebar to update
      apolloClient.refetchQueries({
        include: [GET_DEAL_FIELD_GROUPS],
      });

      const placeholderLinks = docData?.getDocumentById?.placeholderLinks || [];

      // Sync ALL linked placeholders to the editor.
      // This mirrors what PlaceholderItem's useEffect does on mount.
      for (const link of placeholderLinks) {
        if (link.dealFieldGroup) {
          const uuid = link.placeholder?.uuid;
          const placeholderName = link.placeholder?.title;
          const newValue = link.dealFieldGroup?.value || '';
          if (uuid || placeholderName) {
            window.dispatchEvent(
              new CustomEvent('updatePlaceholderValue', {
                detail: { placeholderId: uuid, placeholderName, newValue },
              })
            );
          }
        }
      }
    } catch (error) {
      // Query failed - silently continue, user can switch tabs to trigger sync
    }
  };

  // Handle creating a new placeholder
  const handleCreatePlaceholder = useCallback(async (name: string, isRequired: boolean) => {
    setIsCreating(true);
    try {
      const result = await createDocumentPlaceholder({
        variables: {
          title: name,
          isRequired,
          linkableId: documentId,
          linkableType: 'Document',
          teamId,
        },
      });

      const newPlaceholder = result.data?.createDocumentPlaceholder;
      if (newPlaceholder?.uuid) {
        // Dispatch event to CKEditor to insert the placeholder at cursor position
        window.dispatchEvent(
          new CustomEvent('insertPlaceholderAtCursor', {
            detail: {
              uuid: newPlaceholder.uuid,
              name: newPlaceholder.title,
              required: newPlaceholder.isRequired,
            },
          })
        );
      }

      setShowCreateModal(false);
    } catch (error) {
      // Silent failure - user can retry
    } finally {
      setIsCreating(false);
    }
  }, [createDocumentPlaceholder, documentId, teamId]);

  // Listen for focus events from CKEditor - switch to "This Document" tab
  useEffect(() => {
    const handleFocusPlaceholder = () => {
      setActiveTab(PlaceholdersSidebarTab.documentFields);
    };

    window.addEventListener('focusPlaceholderInSidebar', handleFocusPlaceholder);
    return () => {
      window.removeEventListener('focusPlaceholderInSidebar', handleFocusPlaceholder);
    };
  }, []);

  // Listen for external requests to open the create placeholder modal (e.g., Insert > Placeholder menu)
  useEffect(() => {
    const handleOpenCreateModal = () => {
      if (!isReadOnly && !isCounterparty && canInsertAtCursor && canCreatePlaceholders) {
        setShowCreateModal(true);
      }
    };

    window.addEventListener('openCreatePlaceholderModal', handleOpenCreateModal);
    return () => {
      window.removeEventListener('openCreatePlaceholderModal', handleOpenCreateModal);
    };
  }, [isReadOnly, isCounterparty, canInsertAtCursor, canCreatePlaceholders]);

  // Listen for CKEditor placeholder command state changes and request initial state
  // IMPORTANT: Attach listener BEFORE emitting ready event to avoid race condition
  useEffect(() => {
    const handleCommandStateChange = (event: CustomEvent) => {
      const { canInsert } = event.detail || {};
      setCanInsertAtCursor(canInsert !== false);
    };

    // Attach listener first
    window.addEventListener('placeholderCommandStateChange', handleCommandStateChange as EventListener);

    // Then request current state from CKEditor
    window.dispatchEvent(new CustomEvent('placeholdersSidebarReady'));

    return () => {
      window.removeEventListener('placeholderCommandStateChange', handleCommandStateChange as EventListener);
    };
  }, []);

  const renderTabContent = () => {
    // Counterparty only sees "This Document" content - no deal fields tab
    if (isCounterparty) {
      return <DocumentFieldsList documentId={documentId} dealId={dealId} teamId={teamId} isReadOnly={isReadOnly} isCounterparty={isCounterparty} canLinkContacts={canCreatePlaceholders} onShowUpgradeModal={() => setShowUpgradeModal(true)} />;
    }

    switch (activeTab) {
      case PlaceholdersSidebarTab.documentFields:
        return <DocumentFieldsList documentId={documentId} dealId={dealId} teamId={teamId} isReadOnly={isReadOnly} isCounterparty={isCounterparty} canLinkContacts={canCreatePlaceholders} onShowUpgradeModal={() => setShowUpgradeModal(true)} />;
      case PlaceholdersSidebarTab.dealFields:
        return <DealFieldsList documentId={documentId} dealId={dealId} teamId={teamId} isReadOnly={isReadOnly} canLinkContacts={canCreatePlaceholders} onShowUpgradeModal={() => setShowUpgradeModal(true)} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Container className={styles.sidebarContainer} data-placeholders-sidebar>
        <div className={styles.stickyHeader}>
          <div className={styles.sidebarHeaderRow}>
            <h2>Placeholders</h2>
            {/* Add placeholder button - hidden for counterparty and read-only */}
            {!isReadOnly && !isCounterparty && (
              <button
                className={styles.addPlaceholderButton}
                onClick={() => {
                  if (!canInsertAtCursor) {
                    // Cursor is not in an editable area (restricted editing mode)
                    return;
                  }
                  if (canCreatePlaceholders) {
                    setShowCreateModal(true);
                  } else {
                    setShowUpgradeModal(true);
                  }
                }}
                title={canInsertAtCursor ? "Add new placeholder" : "Move cursor to an editable area to add placeholder"}
                disabled={!canInsertAtCursor}
                style={{ opacity: canInsertAtCursor ? 1 : 0.4, cursor: canInsertAtCursor ? 'pointer' : 'not-allowed' }}
              >
                +
              </button>
            )}
          </div>
          {/* Hide tabs for counterparty - they only see "This Document" */}
          {!isCounterparty && <TabToggleButtons activeTab={activeTab} setActiveTab={setActiveTab} />}
        </div>
        <div className={styles.tabContent}>{renderTabContent()}</div>
        <ContactPickerOverlay />
      </Container>
      {/* One history panel for the whole sidebar: it portals over the app, so
          mounting it per card would stack one per expanded field. */}
      {historyTarget && <FieldHistorySlideout target={historyTarget} onClose={closeHistory} />}
      {/* Render ContactSlideout when a contact is being edited */}
      {editContactData && (
        <ContactSlideout
          contact={editContactData}
          onClose={closeEditContact}
          onSave={handleContactSave}
        />
      )}
      {/* Create Placeholder Modal */}
      <CreatePlaceholderModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreatePlaceholder}
        isCreating={isCreating}
      />
      {/* Upgrade Modal - shown when free/basic users try to create placeholders */}
      <SubscriptionRequiredModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </>
  );
};

const PlaceholdersSidebarContainer: React.FC<PlaceholdersSidebarContainerProps> = (props) => {
  return (
    <ContactPickerProvider>
      <FieldHistoryProvider>
        <PlaceholdersSidebarContent {...props} />
      </FieldHistoryProvider>
    </ContactPickerProvider>
  );
};

export default PlaceholdersSidebarContainer;
